import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  TextField,
  FormControlLabel,
  Switch,
  ListItemText,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useSnackbar } from 'notistack'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import { getGeneric, postGeneric } from '../../common/constants'
import CopyIcon from '../../common/CopyIcon'

// ── band -> MUI palette colour (matches AddressQualityCard) ──
const BAND_COLOR = { EXCELLENT: 'success', GOOD: 'info', FAIR: 'warning', POOR: 'error' }

// ── the four confusion-matrix quadrants + the pending bucket ──
const VERDICT = {
  TRUE_NEGATIVE: { short: 'TRUE −', label: 'Correct · safe', color: 'success' },
  FALSE_NEGATIVE: { short: 'FALSE −', label: 'Missed risk', color: 'error' },
  FALSE_POSITIVE: { short: 'FALSE +', label: 'Over-flagged', color: 'warning' },
  TRUE_POSITIVE: { short: 'TRUE +', label: 'Correct · risky', color: 'success' },
  PENDING: { short: 'PENDING', label: 'Outcome pending', color: 'default' },
}

// ── structured feedback reason codes, contextual to the quadrant ──
// reason CORRECT => verdict ACCURATE, everything else => INACCURATE.
const REASONS_BY_VERDICT = {
  FALSE_NEGATIVE: [
    { code: 'SCORE_TOO_HIGH_ADDRESS_BAD', label: 'Score too high — address was bad' },
    { code: 'OUTCOME_NOT_ADDRESS_RELATED', label: 'Failed, but not the address' },
    { code: 'CORRECT', label: 'Prediction was actually right' },
  ],
  FALSE_POSITIVE: [
    { code: 'SCORE_TOO_LOW_ADDRESS_FINE', label: 'Score too low — address was fine' },
    { code: 'RECOVERED_AFTER_CORRECTION', label: 'Delivered only after address fix' },
    { code: 'CORRECT', label: 'Prediction was actually right' },
  ],
  TRUE_POSITIVE: [
    { code: 'CORRECT', label: 'Confirmed — correctly flagged' },
    { code: 'OUTCOME_NOT_ADDRESS_RELATED', label: 'Failed, but not the address' },
  ],
  TRUE_NEGATIVE: [
    { code: 'CORRECT', label: 'Confirmed — correctly cleared' },
    { code: 'SCORE_TOO_LOW_ADDRESS_FINE', label: 'Score lower than it should be' },
  ],
}
const ALL_REASONS = [
  { code: 'CORRECT', label: 'Prediction was right' },
  { code: 'SCORE_TOO_HIGH_ADDRESS_BAD', label: 'Score too high — address was bad' },
  { code: 'SCORE_TOO_LOW_ADDRESS_FINE', label: 'Score too low — address was fine' },
  { code: 'OUTCOME_NOT_ADDRESS_RELATED', label: 'Failed, but not the address' },
  { code: 'RECOVERED_AFTER_CORRECTION', label: 'Delivered only after address fix' },
]

const RANGE_OPTIONS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
]

const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`)
const num = (v) => (v === null || v === undefined ? 0 : v).toLocaleString('en-IN')

// build a query-param object with only the keys that have a value
const params = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v !== null && v !== undefined))

const KpiCard = ({ label, value, note }) => (
  <Card variant='outlined' sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {label}
      </Typography>
      <Typography variant='h4' fontWeight={700} sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {note && (
        <Typography variant='caption' color='text.secondary'>
          {note}
        </Typography>
      )}
    </CardContent>
  </Card>
)

// one clickable matrix cell
const MatrixCell = ({ verdict, count, active, onClick }) => {
  const meta = VERDICT[verdict]
  const palette = { success: '#e9f5ec', warning: '#fbf0e1', error: '#fae9e9' }
  const fg = { success: '#15803d', warning: '#b45309', error: '#b91c1c' }
  return (
    <Box
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      sx={{
        cursor: 'pointer',
        borderRadius: 1.5,
        p: 1.75,
        minHeight: 96,
        bgcolor: palette[meta.color] || '#eef1f4',
        border: '2px solid',
        borderColor: active ? 'text.primary' : 'transparent',
        transition: 'transform .08s',
        '&:hover': { transform: 'translateY(-1px)' },
      }}
    >
      <Typography variant='caption' fontWeight={700} sx={{ color: fg[meta.color], textTransform: 'uppercase' }}>
        {meta.label}
      </Typography>
      <Typography variant='h4' fontWeight={700} sx={{ color: fg[meta.color] }}>
        {num(count)}
      </Typography>
    </Box>
  )
}

const ScoreBar = ({ label, value, color }) => (
  <Box sx={{ mb: 1.25 }}>
    <Stack direction='row' justifyContent='space-between'>
      <Typography variant='body2' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='body2' fontWeight={700}>
        {pct(value)}
      </Typography>
    </Stack>
    <LinearProgress
      variant='determinate'
      value={Math.max(0, Math.min(100, value || 0))}
      color={color}
      sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
    />
  </Box>
)

const Formula = ({ children }) => (
  <Box
    sx={{
      fontFamily: 'monospace',
      fontSize: 13,
      bgcolor: 'action.hover',
      borderRadius: 1,
      px: 1.5,
      py: 1,
      my: 1,
      whiteSpace: 'nowrap',
      overflowX: 'auto',
    }}
  >
    {children}
  </Box>
)

// plain-language explainer for the scorecard metrics, with live numbers when available
const ScorecardInfoDialog = ({ open, onClose, summary }) => {
  const tp = summary?.truePositive
  const fp = summary?.falsePositive
  const fn = summary?.falseNegative
  const tn = summary?.trueNegative
  const hasCounts = [tp, fp, fn, tn].every((v) => v !== null && v !== undefined)

  const Metric = ({ title, value, question, formula, live, meaning }) => (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction='row' spacing={1} alignItems='baseline'>
        <Typography variant='subtitle2' fontWeight={700}>
          {title}
        </Typography>
        <Typography variant='subtitle2' color='text.secondary'>
          {pct(value)}
        </Typography>
      </Stack>
      <Typography variant='body2' sx={{ mt: 0.5 }}>
        {question}
      </Typography>
      <Formula>{formula}</Formula>
      {hasCounts && (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
          With the current filters: {live}
        </Typography>
      )}
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
        {meaning}
      </Typography>
    </Box>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Understanding the scorecard</DialogTitle>
      <DialogContent dividers>
        <Typography variant='body2' sx={{ mb: 1 }}>
          Netra looks at every address and makes a call: <b>risky</b> (FAIR/POOR band — “this delivery may fail”) or{' '}
          <b>safe</b> (EXCELLENT/GOOD — “this should deliver fine”). Later, reality tells us what actually happened:
          the order either <b>failed</b> (address-related NDR or RTO) or <b>delivered</b>. Comparing the prediction
          with the outcome gives four buckets:
        </Typography>
        <Typography variant='body2' component='ul' sx={{ pl: 3, mb: 2 }}>
          <li>
            <b>Correct · risky</b> — flagged risky, and it really failed{hasCounts ? ` (${num(tp)})` : ''}
          </li>
          <li>
            <b>Over-flagged</b> — flagged risky, but it delivered fine{hasCounts ? ` (${num(fp)})` : ''}
          </li>
          <li>
            <b>Missed risk</b> — called safe, but it failed{hasCounts ? ` (${num(fn)})` : ''}
          </li>
          <li>
            <b>Correct · safe</b> — called safe, and it delivered{hasCounts ? ` (${num(tn)})` : ''}
          </li>
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Metric
          title='Precision'
          value={summary?.precision}
          question='When Netra raises an alarm, how often is it right?'
          formula='Precision = Correct·risky ÷ (Correct·risky + Over-flagged)'
          live={hasCounts ? `${num(tp)} ÷ (${num(tp)} + ${num(fp)}) = ${pct(summary?.precision)}` : ''}
          meaning='Low precision means many alarms are false — teams waste effort re-checking addresses that were actually fine.'
        />
        <Metric
          title='Recall'
          value={summary?.recall}
          question='Of the deliveries that really failed, how many had Netra warned about?'
          formula='Recall = Correct·risky ÷ (Correct·risky + Missed risk)'
          live={hasCounts ? `${num(tp)} ÷ (${num(tp)} + ${num(fn)}) = ${pct(summary?.recall)}` : ''}
          meaning='Low recall means most real failures slipped through with a “safe” rating — the model is missing risk.'
        />
        <Metric
          title='Accuracy'
          value={summary?.accuracy}
          question='Across all decided orders, how often did the prediction match the outcome?'
          formula='Accuracy = (Correct·risky + Correct·safe) ÷ all decided orders'
          live={
            hasCounts
              ? `(${num(tp)} + ${num(tn)}) ÷ ${num(tp + tn + fp + fn)} = ${pct(summary?.accuracy)}`
              : ''
          }
          meaning='Read this one with care: since most orders deliver fine, simply calling everything “safe” already scores high. Precision and recall are the honest measures.'
        />

        <Divider sx={{ mb: 1.5 }} />
        <Typography variant='caption' color='text.secondary'>
          Only orders with a known outcome are counted — shipments still in transit (“pending”) are excluded until
          they deliver or fail.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Got it</Button>
      </DialogActions>
    </Dialog>
  )
}

const AddressQualityDashboard = ({ embedded = false }) => {
  const { enqueueSnackbar } = useSnackbar()

  // filters
  const [rangeDays, setRangeDays] = useState(30)
  const [merchantId, setMerchantId] = useState('')
  const [band, setBand] = useState('')
  const [disagreementsOnly, setDisagreementsOnly] = useState(false)
  const [verdict, setVerdict] = useState('') // selected matrix quadrant

  // table-level filters (shipments query only)
  const [searchInput, setSearchInput] = useState('') // what the user is typing
  const [search, setSearch] = useState('') // debounced value sent to the API
  const [outcome, setOutcome] = useState('')

  // data
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [rowCount, setRowCount] = useState(0)
  const [gridLoading, setGridLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)

  // feedback menu
  const [fbAnchor, setFbAnchor] = useState(null)
  const [fbRow, setFbRow] = useState(null)

  // scorecard explainer dialog
  const [scorecardInfoOpen, setScorecardInfoOpen] = useState(false)

  const window = useMemo(() => {
    const to = Date.now()
    const from = to - rangeDays * 24 * 60 * 60 * 1000
    return { from, to }
  }, [rangeDays])

  const baseFilters = useMemo(
    () => ({ from: window.from, to: window.to, merchantId, band }),
    [window, merchantId, band],
  )

  const loadSummary = useCallback(() => {
    setSummaryLoading(true)
    getGeneric('netra/quality/summary', params(baseFilters))
      .then((r) => setSummary(r?.data))
      .catch(() => enqueueSnackbar('Could not load the quality summary', { variant: 'error' }))
      .finally(() => setSummaryLoading(false))
  }, [baseFilters, enqueueSnackbar])

  // debounce the AWB/order search so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const loadShipments = useCallback(() => {
    setGridLoading(true)
    getGeneric(
      'netra/quality/shipments',
      params({
        ...baseFilters,
        verdict,
        disagreementsOnly: disagreementsOnly || undefined,
        search,
        outcome,
        page,
        size: sizePerPage,
      }),
    )
      .then((r) => {
        setRows(r?.data?.content || [])
        setRowCount(r?.data?.totalElements || 0)
      })
      .catch(() => enqueueSnackbar('Could not load shipments', { variant: 'error' }))
      .finally(() => setGridLoading(false))
  }, [baseFilters, verdict, disagreementsOnly, search, outcome, page, sizePerPage, enqueueSnackbar])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])
  useEffect(() => {
    loadShipments()
  }, [loadShipments])

  // reset to first page whenever a filter that changes the result set changes
  useEffect(() => {
    setPage(0)
  }, [rangeDays, merchantId, band, verdict, disagreementsOnly, search, outcome])

  const selectQuadrant = (q) => {
    setDisagreementsOnly(false)
    setVerdict((prev) => (prev === q ? '' : q))
  }

  const openFeedback = (e, row) => {
    e.stopPropagation()
    setFbAnchor(e.currentTarget)
    setFbRow(row)
  }
  const closeFeedback = () => {
    setFbAnchor(null)
    setFbRow(null)
  }
  const submitFeedback = (reason) => {
    if (!fbRow) return
    const body = {
      verdict: reason.code === 'CORRECT' ? 'ACCURATE' : 'INACCURATE',
      reasonCode: reason.code,
    }
    const orderId = fbRow.orderId
    postGeneric(`netra/quality/${orderId}/feedback`, body)
      .then((r) => {
        const updated = r?.data
        setRows((prev) => prev.map((x) => (x.orderId === orderId ? { ...x, ...updated } : x)))
        enqueueSnackbar('Feedback recorded', { variant: 'success' })
      })
      .catch(() => enqueueSnackbar('Could not record feedback', { variant: 'error' }))
      .finally(closeFeedback)
  }

  const columns = useMemo(
    () => [
      {
        field: 'awb',
        headerName: 'AWB / Order',
        width: 170,
        renderCell: ({ row }) => (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {row.awb ? (
                <Typography
                  variant='body2'
                  fontFamily='monospace'
                  component='a'
                  href={`/order-detail/${row.awb}?id=${row.orderId}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={(e) => e.stopPropagation()}
                  sx={{ color: '#3783e7', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {row.awb}
                </Typography>
              ) : (
                <Typography variant='body2' fontFamily='monospace'>
                  —
                </Typography>
              )}
              {row.awb ? <CopyIcon text={row.awb} /> : null}
            </Box>
            <Typography variant='caption' color='text.secondary' display='block'>
              {row.orderRef || row.orderId}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'scoredAddressLine',
        headerName: 'Scored address',
        width: 240,
        renderCell: ({ row }) => (
          <Box sx={{ py: 0.5 }}>
            <Typography variant='body2' sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}>
              {row.scoredAddressLine || '—'}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {[row.scoredCity, row.scoredState, row.scoredPincode].filter(Boolean).join(', ')}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'score',
        headerName: 'Score',
        width: 130,
        renderCell: ({ row }) => (
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='body2' fontWeight={700} fontFamily='monospace'>
              {row.score != null ? Math.round(row.score) : '—'}
            </Typography>
            {row.band && <Chip size='small' color={BAND_COLOR[row.band] || 'default'} label={row.band} />}
          </Stack>
        ),
      },
      {
        field: 'orderStatus',
        headerName: 'Outcome',
        width: 180,
        renderCell: ({ row }) => (
          <Box>
            <Typography variant='body2'>{row.orderStatus || (row.hadNdr ? 'In NDR' : 'In transit')}</Typography>
            {row.ndrAddressRelated && (
              <Typography variant='caption' color='error.main'>
                {row.ndrReasonCanonical || 'address NDR'}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'outcomeVerdict',
        headerName: 'Verdict',
        width: 130,
        renderCell: ({ row }) => {
          const meta = VERDICT[row.outcomeVerdict] || VERDICT.PENDING
          return <Chip size='small' color={meta.color} variant='outlined' label={meta.short} />
        },
      },
      {
        field: 'feedback',
        headerName: 'Feedback',
        width: 220,
        sortable: false,
        renderCell: ({ row }) =>
          row.feedbackReasonCode || row.feedbackVerdict ? (
            <Tooltip title={`${row.feedbackVerdict || ''} ${row.feedbackBy ? '· ' + row.feedbackBy : ''}`}>
              <Chip
                size='small'
                color='primary'
                icon={<CheckCircleOutlineIcon />}
                label={(row.feedbackReasonCode || row.feedbackVerdict || '').replace(/_/g, ' ').toLowerCase()}
                sx={{ maxWidth: 200 }}
              />
            </Tooltip>
          ) : (
            <Button size='small' variant='outlined' startIcon={<AddCommentOutlinedIcon />} onClick={(e) => openFeedback(e, row)}>
              Give feedback
            </Button>
          ),
      },
    ],
    [],
  )

  const fbReasons = fbRow ? REASONS_BY_VERDICT[fbRow.outcomeVerdict] || ALL_REASONS : ALL_REASONS

  return (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      {!embedded && (
        <>
          <Typography variant='h5' fontWeight={700}>
            Netra · Address Quality
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Address score vs. real delivery outcome — verify accuracy and capture ground-truth feedback.
          </Typography>
        </>
      )}

      {/* ── filters ── */}
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size='small'>
                <InputLabel>Date range</InputLabel>
                <Select label='Date range' value={rangeDays} onChange={(e) => setRangeDays(e.target.value)}>
                  {RANGE_OPTIONS.map((o) => (
                    <MenuItem key={o.days} value={o.days}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                size='small'
                label='Merchant ID (optional)'
                value={merchantId}
                onChange={(e) => setMerchantId(e.target.value.trim())}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size='small'>
                <InputLabel>Band</InputLabel>
                <Select label='Band' value={band} onChange={(e) => setBand(e.target.value)}>
                  <MenuItem value=''>All bands</MenuItem>
                  {['EXCELLENT', 'GOOD', 'FAIR', 'POOR'].map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={disagreementsOnly}
                    onChange={(e) => {
                      setDisagreementsOnly(e.target.checked)
                      if (e.target.checked) setVerdict('')
                    }}
                  />
                }
                label='Disagreements only'
              />
            </Grid>
            {verdict && (
              <Grid item xs={12} md={2}>
                <Chip
                  label={`Filter: ${VERDICT[verdict].label}`}
                  onDelete={() => setVerdict('')}
                  color='primary'
                  variant='outlined'
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* ── KPIs ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <KpiCard label='Orders scored' value={num(summary?.totalScored)} note={summaryLoading ? 'loading…' : ' '} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            label='Outcome decided'
            value={num(summary?.reconciled)}
            note={`${num(summary?.pending)} pending`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard label='Disagreement rate' value={pct(summary?.disagreementRate)} note={`${num(summary?.disagreements)} cases`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard label='Feedback captured' value={num(summary?.feedbackCaptured)} note=' ' />
        </Grid>
      </Grid>

      {/* ── confusion matrix + scorecard ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Card variant='outlined' sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant='subtitle1' fontWeight={700}>
                Score vs. outcome
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                “Risky” = FAIR/POOR · “Failed” = address-related NDR or RTO. Click a cell to filter the table.
              </Typography>
              {summaryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: '70px 1fr 1fr', gap: 1, mt: 2 }}>
                  <Box />
                  <Typography variant='caption' align='center' color='text.secondary' fontWeight={700}>
                    DELIVERED
                  </Typography>
                  <Typography variant='caption' align='center' color='text.secondary' fontWeight={700}>
                    FAILED
                  </Typography>

                  <Typography variant='caption' color='text.secondary' fontWeight={700} sx={{ alignSelf: 'center' }}>
                    SAFE
                  </Typography>
                  <MatrixCell verdict='TRUE_NEGATIVE' count={summary?.trueNegative} active={verdict === 'TRUE_NEGATIVE'} onClick={() => selectQuadrant('TRUE_NEGATIVE')} />
                  <MatrixCell verdict='FALSE_NEGATIVE' count={summary?.falseNegative} active={verdict === 'FALSE_NEGATIVE'} onClick={() => selectQuadrant('FALSE_NEGATIVE')} />

                  <Typography variant='caption' color='text.secondary' fontWeight={700} sx={{ alignSelf: 'center' }}>
                    RISKY
                  </Typography>
                  <MatrixCell verdict='FALSE_POSITIVE' count={summary?.falsePositive} active={verdict === 'FALSE_POSITIVE'} onClick={() => selectQuadrant('FALSE_POSITIVE')} />
                  <MatrixCell verdict='TRUE_POSITIVE' count={summary?.truePositive} active={verdict === 'TRUE_POSITIVE'} onClick={() => selectQuadrant('TRUE_POSITIVE')} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card variant='outlined' sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
                <Typography variant='subtitle1' fontWeight={700}>
                  Model scorecard
                </Typography>
                <Tooltip title='What do these numbers mean?'>
                  <IconButton size='small' onClick={() => setScorecardInfoOpen(true)} aria-label='Explain the scorecard metrics'>
                    <InfoOutlinedIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Stack>
              <ScoreBar label='Precision — of flagged, actually failed' value={summary?.precision} color='warning' />
              <ScoreBar label='Recall — of failures, were flagged' value={summary?.recall} color='success' />
              <ScoreBar label='Accuracy — overall agreement' value={summary?.accuracy} color='primary' />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── drill-down ── */}
      <Card variant='outlined'>
        <CardContent>
          <Typography variant='subtitle1' fontWeight={700} sx={{ mb: 1 }}>
            Shipments
          </Typography>
          <Grid container spacing={1.5} alignItems='center' sx={{ mb: 1.5 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size='small'
                label='Search AWB / Order ref'
                placeholder='e.g. 90574797740 or CAVA/26-27/124727'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <FormControl fullWidth size='small'>
                <InputLabel>Score label</InputLabel>
                <Select label='Score label' value={band} onChange={(e) => setBand(e.target.value)}>
                  <MenuItem value=''>All labels</MenuItem>
                  {['EXCELLENT', 'GOOD', 'FAIR', 'POOR'].map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2.5}>
              <FormControl fullWidth size='small'>
                <InputLabel>Outcome</InputLabel>
                <Select label='Outcome' value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                  <MenuItem value=''>All outcomes</MenuItem>
                  <MenuItem value='DELIVERED'>Delivered</MenuItem>
                  <MenuItem value='RTO'>RTO</MenuItem>
                  <MenuItem value='IN_NDR'>In NDR</MenuItem>
                  <MenuItem value='IN_TRANSIT'>In transit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size='small'>
                <InputLabel>Verdict</InputLabel>
                <Select
                  label='Verdict'
                  value={verdict}
                  onChange={(e) => {
                    setDisagreementsOnly(false)
                    setVerdict(e.target.value)
                  }}
                >
                  <MenuItem value=''>All verdicts</MenuItem>
                  {Object.entries(VERDICT).map(([code, meta]) => (
                    <MenuItem key={code} value={code}>
                      {meta.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Box sx={{ position: 'relative' }}>
            {gridLoading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }} />}
            <ProzoDataGrid
              columns={columns}
              rows={rows}
              rowCount={rowCount}
              page={page}
              setPage={setPage}
              sizePerPage={sizePerPage}
              setSizePerPage={setSizePerPage}
              paginationMode='server'
              getRowId={(r) => r.orderId}
              getRowHeight={() => 'auto'}
            />
          </Box>
        </CardContent>
      </Card>

      {/* ── scorecard explainer ── */}
      <ScorecardInfoDialog open={scorecardInfoOpen} onClose={() => setScorecardInfoOpen(false)} summary={summary} />

      {/* ── feedback menu ── */}
      <Menu anchorEl={fbAnchor} open={Boolean(fbAnchor)} onClose={closeFeedback}>
        <Typography variant='caption' color='text.secondary' sx={{ px: 2, py: 1, display: 'block' }}>
          Why does the score disagree?
        </Typography>
        {fbReasons.map((reason) => (
          <MenuItem key={reason.code} onClick={() => submitFeedback(reason)}>
            <ListItemText primary={reason.label} secondary={reason.code} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export default AddressQualityDashboard
